// src/screens/DadosBancarios/index.tsx
import React, {useEffect, useMemo, useState} from 'react';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';

import {Alert, FlatList, TouchableOpacity, View, Text} from 'react-native';

import {Button} from '../../../components/Interface/Forms/Buttons/Button';
import {Input} from '../../../components/Interface/Forms/Inputs/Input';
import {Picker} from '../../../components/Interface/Forms/Inputs/Picker';
import {Header} from '../../../components/Interface/Headers/Header';
import {Titles} from '../../../components/Interface/Typographies/Titles';
import {PixRefundWarningModal} from '../../../components/shared/PixRefundWarningModal';
import LegacyBottomSheet from '../../../components/Interface/LegacyBottomSheet';
import {SearchInput} from '../../../components/Interface/Forms/Inputs/SearchInput';

import {
  Container,
  Content,
  Form,
  InputGroup,
  BankInputContainer,
  BankDropdownIcon,
  BankSelectorLabel,
  BankSelectorValue,
  BankSheetTitle,
  BankSheetItem,
  BankSheetItemText,
  BankSheetEmpty,
  SearchForm,
  Footer,
  InputRow,
  CardContainer,
  CardTitle,
  TypeContainer,
  CardButtonIcon,
  InputSpan,
  PixWarningText,
} from './styles';

import {RFValue} from 'react-native-responsive-fontsize';
import {NativeStackScreenProps} from 'react-native-screens/lib/typescript/native-stack/types';
import {RootStackParamList} from '../../../dtos/RoutesDTO';

import theme from '../../../global/styles/legacy/theme';
import { SafeAreaView } from 'react-native-safe-area-context';
import {api} from '../../../services/api';

type ScreenProps = NativeStackScreenProps<RootStackParamList, 'DadosBancarios'>;

/* -------------------------------------------------------------------------- */
/* utilidades                                                                 */
/* -------------------------------------------------------------------------- */
const MASKS: Record<string, string | undefined> = {
  cpf: '[000].[000].[000]-[00]',
  cnpj: '[00].[000].[000]/[0000]-[00]',
  telefone: '([00]) [00000]-[0000]',
  email: undefined,
  chaveAleatoria: undefined,
};

const INSCRICAO_MASKS: Record<'cpf' | 'cnpj', string> = {
  cpf: '[000].[000].[000]-[00]',
  cnpj: '[00].[000].[000]/[0000]-[00]',
};


const LIMPA_TUDO_EXCETO_DIGITOS = /\D/g;
const LIMPA_TUDO_EXCETO_LETRAS_E_NUMEROS = /[^a-zA-Z0-9]/g;
const REMOVE_ESPACOS = /\s+/g;

const stripMask = (v: string) =>
  v.replace(LIMPA_TUDO_EXCETO_LETRAS_E_NUMEROS, '');
const stripDigits = (v: string) => v.replace(LIMPA_TUDO_EXCETO_DIGITOS, '');
const stripSpaces = (v: string) => v.replace(REMOVE_ESPACOS, '');

const PIX_PF_WARNING_TEXT =
  'Informe a chave PIX da conta que realizou o pagamento.';
const PIX_PF_WARNING_BODY =
  'Essa confirmação é necessária para garantir que o reembolso seja enviado para o titular correto.';

type BankOption = {
  label: string;
  value: string;
};

const normalizeBankCode = (code: unknown) => {
  if (code === null || code === undefined) {return '';}
  const codeStr = String(code).trim();
  if (/^\d{1,2}$/.test(codeStr)) {return codeStr.padStart(3, '0');}
  return codeStr;
};

const buildBankOptions = (data: unknown): BankOption[] => {
  const toTitleCase = (value: string) =>
    value
      .toLowerCase()
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  if (Array.isArray(data)) {
    return data
      .map((item: any) => {
        const code = normalizeBankCode(
          item.codigo ?? item.code ?? item.numero ?? item.value ?? item.id,
        );
        const rawName = String(
          item.nome ?? item.name ?? item.banco ?? item.label ?? '',
        ).trim();
        const name = toTitleCase(rawName);
        const label = code && name ? `${code} ${name}` : name || code;
        return label ? {label, value: code || label} : null;
      })
      .filter((item: BankOption | null): item is BankOption => Boolean(item));
  }

  if (data && typeof data === 'object') {
    return Object.entries(data as Record<string, unknown>)
      .map(([code, name]) => {
        const codeStr = normalizeBankCode(code);
        const nameStr = toTitleCase(String(name ?? '').trim());
        const label = codeStr && nameStr ? `${codeStr} ${nameStr}` : nameStr || codeStr;
        return label ? {label, value: codeStr || label} : null;
      })
      .filter((item: BankOption | null): item is BankOption => Boolean(item));
  }

  return [];
};

/* -------------------------------------------------------------------------- */

export function DadosBancarios({navigation, route}: ScreenProps) {
  const {item} = route.params;

  /* ---------- estados ---------- */
  const [banco, setBanco] = useState('');
  const [bankSheetOpen, setBankSheetOpen] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [chavePix, setChavePix] = useState('');
  const [confirmaChavePix, setConfirmaChavePix] = useState('');
  const [agencia, setAgencia] = useState('');
  const [digitoAgencia, setDigitoAgencia] = useState('');
  const [conta, setConta] = useState('');
  const [digitoConta, setDigitoConta] = useState('');
  const [cpfFavorecido, setCpfFavorecido] = useState('');
  const [nomeFavorecido, setNomeFavorecido] = useState('');
  const [tipoChavePix, setTipoChavePix] = useState('');
  const [bankOptions, setBankOptions] = useState<BankOption[]>([]);
  const [tipoInscricaoFav, setTipoInscricaoFav] = useState<
    'cpf' | 'cnpj' | undefined
  >('cpf');
  const [tipoConta, setTipoConta] = useState('');
  const [tipoTransferencia, setTipoTransferencia] = useState<
    'pix' | 'transferencia'
  >();
  const [showPixPfModal, setShowPixPfModal] = useState(false);
  const [pixPfAcknowledged, setPixPfAcknowledged] = useState(false);

  // Estados para controlar o fluxo passo a passo
  const [currentStep, setCurrentStep] = useState<
    'type' | 'pix' | 'account' | 'beneficiary'
  >('type');





  const filteredBanks = useMemo(
    () =>
      bankSearch.trim()
        ? bankOptions.filter(b =>
            b.label.toLowerCase().includes(bankSearch.toLowerCase()),
          )
        : bankOptions,
    [bankOptions, bankSearch],
  );

  const closeBankSheet = () => {
    setBankSheetOpen(false);
    setBankSearch('');
  };

  /* ---------- handlers ---------- */
  useEffect(() => {
    let isMounted = true;

    const loadBanks = async () => {
      try {
        const response = await api.get('/get_bancos');
        const options = buildBankOptions(response.data)
          .slice()
          .sort((a, b) => {
            const getName = (label: string) =>
              label.replace(/^\d+\s+/, '').trim();
            const nameA = getName(a.label);
            const nameB = getName(b.label);
            const byName = nameA.localeCompare(nameB, 'pt-BR');
            return byName !== 0 ? byName : a.label.localeCompare(b.label, 'pt-BR');
          });

        if (isMounted) {
          setBankOptions(options);
        }
      } catch (err) {
        Alert.alert(
          'Ocorreu um problema',
          'Não foi possível listar os bancos disponíveis, tente novamente mais tarde.',
        );
      }
    };

    loadBanks();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (tipoChavePix !== 'cpf') {
      setShowPixPfModal(false);
      setPixPfAcknowledged(false);
    }
  }, [tipoChavePix]);

  const handleTipoChaveChange = (v: string) => {
    setTipoChavePix(v);
    setChavePix('');
    setConfirmaChavePix('');
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'type':
        return 'Tipo de transferência';
      case 'pix':
        return 'Chave Pix';
      case 'account':
        return 'Dados da conta';
      case 'beneficiary':
        return 'Dados do favorecido';
      default:
        return '';
    }
  };

  const getStepSubtitle = () => {
    switch (currentStep) {
      case 'type':
        return 'Como você quer receber?';
      case 'pix':
        return 'Configure sua chave Pix:';
      case 'account':
        return 'Informe os dados da conta e o banco:';
      case 'beneficiary':
        return 'Informe os dados do favorecido:';
      default:
        return '';
    }
  };

  const getButtonText = () => {
    if (currentStep === 'beneficiary') {
      return 'Finalizar';
    }
    return 'Próximo';
  };

  const handleTipoInscricaoChange = (v: 'cpf' | 'cnpj') => {
    setTipoInscricaoFav(v);
    setCpfFavorecido('');
  };

  const handlePayment = () => {
    let chaveLimpa = chavePix.trim();

    if (['cpf', 'cnpj', 'telefone'].includes(tipoChavePix)) {
      chaveLimpa = stripMask(chaveLimpa);
    } else if (tipoChavePix === 'email') {
      chaveLimpa = stripSpaces(chaveLimpa);
    }

    // Se a chave PIX for CPF ou CNPJ, usar ela como dados do favorecido
    let cpfFavorecidoFinal = stripDigits(cpfFavorecido);
    let tipoInscricaoFinal = tipoInscricaoFav?.toUpperCase() as 'CPF' | 'CNPJ';

    if (tipoChavePix === 'cpf') {
      cpfFavorecidoFinal = chaveLimpa;
      tipoInscricaoFinal = 'CPF';
    } else if (tipoChavePix === 'cnpj') {
      cpfFavorecidoFinal = chaveLimpa;
      tipoInscricaoFinal = 'CNPJ';
    }

    if(!item){
      return;
    }

    navigation.navigate('ConfirmaReembolso', {
      item,
      banco,
      chavePix: chaveLimpa,
      tipoChavePix,
      agencia,
      digitoAgencia,
      conta,
      digitoConta,
      cpfFavorecido: cpfFavorecidoFinal,
      nomeFavorecido,
      tipoInscricao: tipoInscricaoFinal,
      tipoConta,
    });
  };

  const resetForm = () => {
    setBanco('');
    setChavePix('');
    setConfirmaChavePix('');
    setAgencia('');
    setDigitoAgencia('');
    setConta('');
    setDigitoConta('');
    setCpfFavorecido('');
    setNomeFavorecido('');
    setTipoChavePix('');
    setTipoInscricaoFav('cpf');
    setTipoConta('');
    setTipoTransferencia(undefined);
    setShowPixPfModal(false);
    setPixPfAcknowledged(false);
  };

  const handleGoBack = () => {
    if (currentStep === 'type') {
      navigation.goBack();
    } else if (currentStep === 'pix' && tipoTransferencia === 'pix') {
      setCurrentStep('type');
      resetForm();
    } else if (currentStep === 'account') {
      if (tipoTransferencia === 'pix') {
        setCurrentStep('pix');
      } else {
        setCurrentStep('type');
        resetForm();
      }
    } else if (currentStep === 'beneficiary') {
      setCurrentStep('account');
    }
  };

  const handleNextStep = () => {
    if (currentStep === 'pix') {
      if (tipoTransferencia === 'pix' && tipoChavePix === 'cpf' && !pixPfAcknowledged) {
        setShowPixPfModal(true);
        return;
      }
      setCurrentStep('account');
    } else if (currentStep === 'account') {
      setCurrentStep('beneficiary');
    } else if (currentStep === 'beneficiary') {
      handlePayment();
    }
  };

  const handleConfirmPixPfModal = () => {
    setShowPixPfModal(false);
    setPixPfAcknowledged(true);
    setCurrentStep('account');
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 'type':
        return !!tipoTransferencia;
      case 'pix':
        return !!tipoChavePix && !!chavePix && chavePix === confirmaChavePix;
      case 'account':
        return !!banco && !!tipoConta && !!agencia && !!conta;
      case 'beneficiary':
        // Se a chave PIX for CPF ou CNPJ, não precisamos validar tipoInscricaoFav e cpfFavorecido
        if (tipoChavePix === 'cpf' || tipoChavePix === 'cnpj') {
          return !!nomeFavorecido;
        }
        return !!tipoInscricaoFav && !!cpfFavorecido && !!nomeFavorecido;
      default:
        return false;
    }
  };

  /* ---------------------------------------------------------------------- */
  /* UI                                                                     */
  /* ---------------------------------------------------------------------- */
  return (
    <SafeAreaView edges={['bottom']} style={{flex: 1}}>
      <LegacyBottomSheet isOpen={bankSheetOpen} onClose={closeBankSheet}>
        <BankSheetTitle>Selecione um banco</BankSheetTitle>
        <SearchForm>
          <SearchInput
            placeholder="Buscar por código ou nome..."
            value={bankSearch}
            onChangeText={setBankSearch}
            autoFocus
            returnKeyType="search"
          />
        </SearchForm>
        <FlatList
          data={filteredBanks}
          keyExtractor={item => item.value}
          style={{maxHeight: RFValue(250)}}
          keyboardShouldPersistTaps="handled"
          renderItem={({item}) => (
            <BankSheetItem
              onPress={() => {
                setBanco(item.value);
                closeBankSheet();
              }}>
              <BankSheetItemText>{item.label}</BankSheetItemText>
            </BankSheetItem>
          )}
          ListEmptyComponent={
            <BankSheetEmpty>Nenhum banco encontrado</BankSheetEmpty>
          }
        />
      </LegacyBottomSheet>

      <PixRefundWarningModal
        visible={showPixPfModal}
        onClose={() => setShowPixPfModal(false)}
        onConfirm={handleConfirmPixPfModal}
        highlightText={PIX_PF_WARNING_TEXT}
        bodyText={PIX_PF_WARNING_BODY}
      />

      <KeyboardAwareScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{paddingBottom: RFValue(20)}}>
        <Container>
          <Header icon="chevron-left" action={handleGoBack} />

          <Content>
            {/* Indicador de progresso */}
            {currentStep !== 'type' && (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: RFValue(20),
                  paddingHorizontal: RFValue(20),
                }}>
                {['pix', 'account', 'beneficiary'].map((step, index) => {
                  const isActive = currentStep === step;
                  const isCompleted =
                    ['pix', 'account', 'beneficiary'].indexOf(currentStep) >
                    index;
                  const shouldShow =
                    tipoTransferencia === 'pix' || step !== 'pix';

                  const label = tipoTransferencia === 'pix' ? index + 1 : index;

                  if (!shouldShow) {return null;}

                  return (
                    <View
                      key={step}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}>
                      <View
                        style={{
                          width: RFValue(24),
                          height: RFValue(24),
                          borderRadius: RFValue(12),
                          backgroundColor:
                            isActive || isCompleted
                              ? theme.colors.primary
                              : theme.colors.border,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}>
                        {isCompleted ? (
                          <Text style={{color: 'white', fontSize: RFValue(12)}}>
                            ✓
                          </Text>
                        ) : (
                          <Text style={{color: 'white', fontSize: RFValue(10)}}>
                            {label}
                          </Text>
                        )}
                      </View>
                      {index < 2 && (
                        <View
                          style={{
                            width: RFValue(20),
                            height: RFValue(2),
                            backgroundColor: isCompleted
                              ? theme.colors.primary
                              : theme.colors.border,
                          }}
                        />
                      )}
                    </View>
                  );
                })}
              </View>
            )}

            <Titles title={getStepTitle()} subtitle={getStepSubtitle()} />

            {/* Step 1: Escolha do tipo de transferência */}
            {currentStep === 'type' && (
              <>
                <TypeContainer>
                  <CardContainer
                    underlayColor={theme.colors.button_click_bg}
                    onPress={() => {
                      setTipoTransferencia('pix');
                      setCurrentStep('pix');
                    }}>
                    <>
                      <CardTitle>Pix</CardTitle>
                      <CardButtonIcon name="chevron-right" />
                    </>
                  </CardContainer>
                </TypeContainer>

                <TypeContainer>
                  <CardContainer
                    underlayColor={theme.colors.button_click_bg}
                    onPress={() => {
                      setTipoChavePix('dadosBancarios');
                      setTipoTransferencia('transferencia');
                      setCurrentStep('account');
                    }}>
                    <>
                      <CardTitle>Transferência bancária</CardTitle>
                      <CardButtonIcon name="chevron-right" />
                    </>
                  </CardContainer>
                </TypeContainer>
              </>
            )}

            {/* Step 2: Configuração do Pix (apenas para PIX) */}
            {currentStep === 'pix' && tipoTransferencia === 'pix' && (
              <Form>
                <InputGroup>
                  <Picker
                    label="Tipo de chave Pix"
                    items={[
                      {label: 'CPF', value: 'cpf'},
                      {label: 'CNPJ', value: 'cnpj'},
                      {label: 'Telefone', value: 'telefone'},
                      {label: 'E‑mail', value: 'email'},
                      {label: 'Chave aleatória', value: 'chaveAleatoria'},
                    ]}
                    placeholder={{
                      label: 'Selecione um tipo de chave',
                      value: '',
                    }}
                    value={tipoChavePix}
                    onChangeValue={handleTipoChaveChange}
                  />
                </InputGroup>

                {!!tipoChavePix && (
                  <>
                    <InputGroup>
                      <Input
                        key={tipoChavePix}
                        labelText="Chave Pix"
                        autoCapitalize="none"
                        placeholder="Digite sua chave Pix"
                        keyboardType={
                          tipoChavePix === 'email'
                            ? 'email-address'
                            : ['cpf', 'cnpj', 'telefone'].includes(tipoChavePix)
                            ? 'number-pad'
                            : 'default'
                        }
                        mask={MASKS[tipoChavePix]}
                        value={chavePix}
                        onChangeText={setChavePix}
                      />
                    </InputGroup>

                    <InputGroup>
                      <Input
                        key={`confirm-${tipoChavePix}`}
                        labelText="Digite novamente a chave Pix"
                        placeholder="Repita sua chave Pix"
                        autoCapitalize="none"
                        keyboardType={
                          tipoChavePix === 'email'
                            ? 'email-address'
                            : ['cpf', 'cnpj', 'telefone'].includes(tipoChavePix)
                            ? 'number-pad'
                            : 'default'
                        }
                        mask={MASKS[tipoChavePix]}
                        value={confirmaChavePix}
                        onChangeText={setConfirmaChavePix}
                      />
                    </InputGroup>
                    {tipoChavePix === 'cpf' && (
                      <PixWarningText>{PIX_PF_WARNING_TEXT}</PixWarningText>
                    )}
                  </>
                )}
              </Form>
            )}

            {/* Step 3: Dados da conta */}
            {currentStep === 'account' && (
              <Form>
                <InputGroup>
                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={() => setBankSheetOpen(true)}>
                    <BankSelectorLabel>Banco</BankSelectorLabel>
                    <BankInputContainer>
                      <BankSelectorValue isPlaceholder={!banco}>
                        {banco
                          ? bankOptions.find(b => b.value === banco)?.label || banco
                          : 'Selecione um banco'}
                      </BankSelectorValue>
                      <BankDropdownIcon name="chevron-down" />
                    </BankInputContainer>
                  </TouchableOpacity>
                </InputGroup>
                <InputGroup>
                  <Picker
                    label="Tipo de conta bancária"
                    items={[
                      {label: 'Corrente', value: '01'},
                      {label: 'Pagamentos', value: '02'},
                      {label: 'Poupança', value: '03'},
                    ]}
                    placeholder={{
                      label: 'Selecione um tipo de conta',
                      value: '',
                    }}
                    value={tipoConta}
                    onChangeValue={setTipoConta}
                  />
                </InputGroup>
                <InputRow>
                  <InputGroup style={{flex: 1, marginRight: RFValue(12)}}>
                    <Input
                      labelText="Agência"
                      placeholder="Digite a agência"
                      keyboardType="number-pad"
                      value={agencia}
                      onChangeText={setAgencia}
                      maxLength={5}
                    />
                  </InputGroup>
                  <InputGroup style={{flex: 1}}>
                    <Input
                      labelText="Dígito Agência"
                      placeholder="Dígito"
                      keyboardType="number-pad"
                      value={digitoAgencia}
                      onChangeText={setDigitoAgencia}
                      maxLength={1}
                    />
                  </InputGroup>
                </InputRow>
                <InputRow>
                  <InputGroup style={{flex: 1, marginRight: RFValue(12)}}>
                    <Input
                      labelText="Conta"
                      placeholder="Digite a conta"
                      keyboardType="number-pad"
                      value={conta}
                      onChangeText={setConta}
                      maxLength={12}
                    />
                  </InputGroup>
                  <InputGroup style={{flex: 1}}>
                    <Input
                      labelText="Dígito Conta"
                      placeholder="Dígito"
                      keyboardType="number-pad"
                      value={digitoConta}
                      onChangeText={setDigitoConta}
                      maxLength={1}
                    />
                  </InputGroup>
                </InputRow>
              </Form>
            )}

            {/* Step 4: Dados do favorecido */}
            {currentStep === 'beneficiary' && (
              <Form>
                {/* Informação quando a chave PIX é CPF ou CNPJ */}
                {(tipoChavePix === 'cpf' || tipoChavePix === 'cnpj') && (
                  <InputSpan style={{marginBottom: RFValue(16)}}>
                    Obs: Como sua chave PIX é um {tipoChavePix.toUpperCase()},
                    não é necessário informar novamente os dados de identificação do favorecido.
                  </InputSpan>
                )}

                {tipoChavePix !== 'cpf' && tipoChavePix !== 'cnpj' && (
                  <InputGroup>
                    <Picker
                      label="Tipo de inscrição do favorecido"
                      items={[
                        {label: 'CPF', value: 'cpf'},
                        {label: 'CNPJ', value: 'cnpj'},
                      ]}
                      placeholder={{
                        label: 'Selecione o tipo de inscrição',
                        value: '',
                      }}
                      value={tipoInscricaoFav}
                      onChangeValue={handleTipoInscricaoChange}
                    />
                  </InputGroup>
                )}

                {!!tipoInscricaoFav &&
                  tipoChavePix !== 'cpf' &&
                  tipoChavePix !== 'cnpj' && (
                    <InputGroup>
                      <Input
                        key={tipoInscricaoFav}
                        labelText={
                          tipoInscricaoFav === 'cpf'
                            ? 'CPF do favorecido'
                            : 'CNPJ do favorecido'
                        }
                        placeholder={
                          tipoInscricaoFav === 'cpf'
                            ? 'Digite o CPF'
                            : 'Digite o CNPJ'
                        }
                        keyboardType="number-pad"
                        mask={INSCRICAO_MASKS[tipoInscricaoFav]}
                        value={cpfFavorecido}
                        onChangeText={setCpfFavorecido}
                        maxLength={tipoInscricaoFav === 'cpf' ? 14 : 18}
                      />
                    </InputGroup>
                  )}

                <InputGroup>
                  <Input
                    labelText="Nome do favorecido"
                    placeholder="Digite o nome completo"
                    keyboardType="default"
                    value={nomeFavorecido}
                    onChangeText={setNomeFavorecido}
                    maxLength={30}
                  />
                </InputGroup>
                <InputSpan>
                  Obs: Se o nome for muito longo (mais de 30 letras), abrevie
                  para caber.
                </InputSpan>
              </Form>
            )}
          </Content>


          {currentStep !== 'type' && (
            <Footer>
              <Button
                title={getButtonText()}
                enabled={canProceedToNextStep()}
                onPress={handleNextStep}
              />
            </Footer>
          )}
        </Container>
      </KeyboardAwareScrollView>

    </SafeAreaView>
  );
}
